// Стилизирана карта с КЛИКАЕМИ пинчета на свободни апартаменти — фон на
// екраните за вход и регистрация. Данните идват от публичния /listings/teaser
// (реални одобрени обяви); ако още няма такива, показваме примерни.
// Докосване на пин отваря карта с детайли + покана за вход.

import React, { useEffect, useState } from 'react';
import { Image, Modal, Pressable, ScrollView, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { api, API_URL, money } from './api';
import { Button, COLORS, Muted } from './ui';

interface Pin {
  id?: string;
  title: string;
  city: string;
  priceCents: number;
  rooms?: number | null;
  description?: string;
  photos?: string[];
  sample?: boolean;
}

const SAMPLE_PINS: Pin[] = [
  { title: 'Двустаен до Джумаята', city: 'Пловдив', priceCents: 45000, rooms: 2, description: 'Светъл, обзаведен, в сърцето на Главната. Примерна обява — реалните се появяват тук след одобрение от модератор.', photos: ['https://picsum.photos/seed/renty-apt1/480/320'], sample: true },
  { title: 'Тристаен в Кючук Париж', city: 'Пловдив', priceCents: 38000, rooms: 3, description: 'Просторен, с гараж и мазе. Примерна обява.', photos: ['https://picsum.photos/seed/renty-apt2/480/320'], sample: true },
  { title: 'Студио в Капана', city: 'Пловдив', priceCents: 52000, rooms: 1, description: 'Дизайнерско студио в квартала на занаятите. Примерна обява.', photos: ['https://picsum.photos/seed/renty-apt3/480/320'], sample: true },
  { title: 'Двустаен в Кършияка', city: 'Пловдив', priceCents: 40000, rooms: 2, description: 'Южно изложение, до Гребната база. Примерна обява.', photos: ['https://picsum.photos/seed/renty-apt4/480/320'], sample: true },
  { title: 'Тристаен под Сахат тепе', city: 'Пловдив', priceCents: 62000, rooms: 3, description: 'Луксозно обзавеждане, паркомясто. Примерна обява.', photos: ['https://picsum.photos/seed/renty-apt5/480/320'], sample: true },
  { title: 'Двустаен в Стария град', city: 'Пловдив', priceCents: 35000, rooms: 2, description: 'Възрожденска къща, автентичен дух. Примерна обява.', photos: ['https://picsum.photos/seed/renty-apt6/480/320'], sample: true },
];

// Позиции ИЗВЪН полетата: горна и долна ивица, разместени (не в една линия)
const SPOTS = [
  { top: 4, left: '2%' },
  { top: 48, left: '35%' },
  { top: 12, right: '2%' },
  { bottom: 48, left: '4%' },
  { bottom: 6, left: '37%' },
  { bottom: 28, right: '2%' },
] as const;

function MapPin({ pin, onPress }: { pin: Pin; onPress: () => void }) {
  // умалената версия — иконата е малка, оригиналът е излишно тежък
  const photo = (pin as any).coverThumb ?? pin.photos?.[0];
  const photoUri = photo ? (photo.startsWith('http') ? photo : `${API_URL}${photo}`) : null;

  return (
    <Pressable onPress={onPress} hitSlop={8} style={({ pressed }) => ({ alignItems: 'center', transform: [{ scale: pressed ? 1.06 : 1 }] })}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          backgroundColor: '#fff',
          borderRadius: 14,
          padding: 4,
          paddingRight: 9,
          borderWidth: 1,
          borderColor: COLORS.border,
          shadowColor: '#000',
          shadowOpacity: 0.2,
          shadowRadius: 6,
          shadowOffset: { width: 0, height: 3 },
          elevation: 4,
        }}
      >
        {photoUri ? (
          <Image source={{ uri: photoUri }} style={{ width: 32, height: 32, borderRadius: 10, backgroundColor: COLORS.bg }} />
        ) : (
          <View style={{ width: 32, height: 32, borderRadius: 10, backgroundColor: COLORS.bg, alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ fontSize: 15 }}>🏠</Text>
          </View>
        )}
        <View>
          <Text style={{ fontWeight: '700', color: COLORS.primary, fontSize: 12 }}>{money(pin.priceCents)}</Text>
          <Text style={{ fontSize: 9, color: COLORS.muted }}>{pin.city} · свободен</Text>
        </View>
      </View>
      <View
        style={{
          width: 0,
          height: 0,
          borderLeftWidth: 6,
          borderRightWidth: 6,
          borderTopWidth: 8,
          borderLeftColor: 'transparent',
          borderRightColor: 'transparent',
          borderTopColor: '#fff',
        }}
      />
      <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: COLORS.primary, marginTop: 2 }} />
    </Pressable>
  );
}

// Слой САМО с пинчетата — рендира се НАД формата (иначе тя поглъща
// докосванията), а извън пиновете всичко минава през него (box-none).
export function MapPins() {
  const insets = useSafeAreaInsets();
  const topInset = insets.top + 8; // под notch-а/статус лентата
  const [pins, setPins] = useState<Pin[]>(SAMPLE_PINS);
  const [selected, setSelected] = useState<Pin | null>(null);

  useEffect(() => {
    api('/listings/teaser')
      .then((r) => {
        if (r.listings?.length) setPins(r.listings);
      })
      .catch(() => {});
  }, []);

  return (
    // pointerEvents като PROP: 'box-none' работи и на web (в style не се поддържа)
    <View
      pointerEvents="box-none"
      style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 }}
    >
      {pins.slice(0, SPOTS.length).map((pin, i) => {
        const spot = SPOTS[i] as { top?: number; bottom?: number; left?: string; right?: string };
        // горните пинчета се изместват под notch-а
        const pos = 'top' in spot ? { ...spot, top: (spot.top ?? 0) + topInset } : spot;
        return (
          <View key={pin.id ?? i} style={[{ position: 'absolute' }, pos as object]}>
            <MapPin pin={pin} onPress={() => setSelected(pin)} />
          </View>
        );
      })}

      {/* детайли за избрания апартамент */}
      <Modal visible={!!selected} transparent animationType="fade" onRequestClose={() => setSelected(null)}>
        <Pressable
          onPress={() => setSelected(null)}
          style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'center', padding: 24 }}
        >
          <Pressable
            style={{ backgroundColor: COLORS.card, borderRadius: 16, padding: 16, maxHeight: '80%' }}
            onPress={() => {}}
          >
            {selected && (
              <ScrollView>
                {selected.photos && selected.photos.length > 0 ? (
                  <ScrollView horizontal style={{ marginBottom: 10 }}>
                    {selected.photos.map((p, i) => {
                      const thumb = (selected as any).thumbs?.[i] ?? p;
                      return (
                        <Image
                          key={p}
                          source={{ uri: thumb.startsWith('http') ? thumb : `${API_URL}${thumb}` }}
                          style={{ width: 200, height: 140, borderRadius: 10, marginRight: 8, backgroundColor: COLORS.border }}
                        />
                      );
                    })}
                  </ScrollView>
                ) : (
                  <View
                    style={{
                      height: 140,
                      borderRadius: 10,
                      marginBottom: 10,
                      backgroundColor: COLORS.bg,
                      borderWidth: 1,
                      borderColor: COLORS.border,
                      borderStyle: 'dashed',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text style={{ fontSize: 32 }}>🏠</Text>
                    <Text style={{ color: COLORS.muted, fontSize: 13 }}>Без снимки</Text>
                  </View>
                )}
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text style={{ fontSize: 18, fontWeight: '700', color: COLORS.text, flex: 1 }}>{selected.title}</Text>
                  <Text style={{ fontSize: 18, fontWeight: '700', color: COLORS.primary }}>
                    {money(selected.priceCents)}
                  </Text>
                </View>
                <Muted>
                  {selected.city}
                  {selected.rooms ? ` · ${selected.rooms} стаи` : ''} · свободен за отдаване
                </Muted>
                {selected.description ? (
                  <Text style={{ color: COLORS.text, marginTop: 8, lineHeight: 20 }}>{selected.description}</Text>
                ) : null}
                <View style={{ marginTop: 12, padding: 10, backgroundColor: COLORS.bg, borderRadius: 10 }}>
                  <Muted>
                    Влезте или се регистрирайте, за да пишете на наемодателя и да видите всички свободни апартаменти.
                  </Muted>
                </View>
                <Button title="Затвори" variant="secondary" onPress={() => setSelected(null)} />
              </ScrollView>
            )}
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

// Декоративната карта — стои ПОД формата и не хваща докосвания
export function MapBackground() {
  return (
    <View
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#E9EEF6',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {/* Черно-бяла карта на центъра на Пловдив (OpenStreetMap) */}
      <Image
        source={require('../assets/plovdiv-map.png')}
        style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, width: '100%', height: '100%', opacity: 0.55 }}
        resizeMode="cover"
      />
      {/* лек воал за четимост на формата */}
      <View style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(244,246,250,0.5)' }} />
    </View>
  );
}
